import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { useStore } from "outstated";
import { AppStore } from "pages/_app.js";
import dayjs from "dayjs";
// mui
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import InputAdornment from "@mui/material/InputAdornment";
import SaveIcon from "@mui/icons-material/Save";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Link from "@mui/material/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ButtonBase from "@mui/material/ButtonBase";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// lib
import formatMoney from "lib/formatMoney";
import scroller from "lib/scroller";
import sleep from "lib/sleep";
// utils
import loadCalculation from "components/Calculators/components/utils/loadCalculation";
// components
import EscrowExplanationTable from "components/Calculators/EscrowCalculator/components/EscrowExplanationTable";
import HUDForm from "components/Calculators/EscrowCalculator/components/HUDForm";
import SpreadLTVTable from "components/Calculators/DSCRCalculator/components/SpreadLTVTable";
import EscrowCalculatorQuestions from "components/Calculators/EscrowCalculator/components/EscrowCalculatorQuestions";
import getDSCRLTVTiers from "components/Loans/utils/getDSCRLTVTiers";
import Authenticator from "components/Authenticator";
import createCalc from "components/Calculators/DSCRCalculator/utils/createCalc";
import updateCalc from "components/Calculators/DSCRCalculator/utils/updateCalc";
import * as gtag from "components/GTagManager";
import dynamic from "next/dynamic";
import SaveSharePDF from "components/Calculators/components/SaveSharePDF";
const AddressAutocomplete = dynamic(() =>
  import("components/AddressAutocomplete")
);

const payoutDict = {
  homeInsurance: "Home insurance",
  mortgageInsurance: "Mortgage insurance",
  countyTax: "County tax",
  cityTax: "City tax",
  schoolTax: "School tax",
  floodInsurance: "Flood insurance",
};

const calcType = "Escrow";

export default function EscrowCalculator({
  externalCalc, // full calc to load from save
  loadCalcs,
  refCalcs,
  externalExampleCalc, // slim calc for example purposes
}) {
  const theme = useTheme();
  const router = useRouter();

  const [suggestionChoice, setSuggestionChoice] = useState(false);
  const [HUDSummary, setHUDSummary] = useState({});
  const { user, userProfile, isNewUser, setSnackbarData } = useStore(AppStore);
  const [loanQuoteData, setLoanQuoteData] = useState({});
  const [escrowResultsData, setEscrowResultsData] = useState([]);
  const [singleItemAnalysisData, setSingleItemAnalysisData] = useState({});

  const [totalLoanLeft, setTotalLoanLeft] = useState(0);
  const [showQuestionDescription, setShowQuestionDescription] = useState({
    "Monthly cashflow": true,
    "Cash to close": true,
  });
  // Answers is what users give and shown in the UI
  const [answers, setAnswers] = useState([]);
  // Questions is what has blueprint of what we ask
  const [questions, setQuestions] = useState([]);
  // All current session answers are held in this
  const [currentValues, setCurrentValues] = useState({});
  const [loanAmount, setLoanAmount] = useState({});
  const [monthlyPmt, setMonthlyPmt] = useState({});
  const [dscr, setDSCR] = useState(0);
  const [cashToClose, setCashToClose] = useState(0);
  const [monthlyCashflow, setMonthlyCashflow] = useState(0);
  const [showSaveFields, setShowSaveFields] = useState(false);
  const [showUpdateFields, setShowUpdateFields] = useState(false);
  const [calc, setCalc] = useState(externalCalc ? externalCalc : null);
  const [showCreatedSuccessMessage, setShowCreatedSuccessMessage] =
    useState(false);
  const [showMeMyRate, setShowMeMyRate] = useState(false);
  // DSCR
  const [creditScore, setCreditScore] = useState(null);
  const [showCopiedSuccess, setShowCopiedSuccess] = useState(false);
  const [anchorEl, setAnchorEl] = useState({});

  // special section that isn't part of original questions
  // but must be added later so we can still capture in 'answers'
  const firstPartOfQuestions = [
    "Loan start",
    "Home insurance",
    "Mortgage insurance",
    "County tax",
    "City tax",
    "School tax",
    "Flood insurance",
  ];
  const secondPartOfQuestions = [
    // "Loan fees",
    // "Monthly rent",
    // "Annual taxes",
    // "Annual insurance",
    // "Annual HOA",
    // TODO loan fees show up next to cash-to-close
    "Origination fee",
    "Loan fees",
    // TODO
    // also add fields Dan mentioned
    "Title fees",
    "Escrow",
    "Prepaid interest",
    "Prepaid insurance",
    // update, to match DFS, we don't use there in our calc
    // "Annual utility expenses",
    // "Annual property management expense",
  ];

  const thirdPart = ["Calculation name", "Address (optional)"];

  const showMeMyRatePart = ["Credit score"];

  const handleOpen = (event, calc) => {
    const updatedAnchorEl = Object.assign({}, anchorEl);
    updatedAnchorEl[calc.id] = event.currentTarget;
    setAnchorEl(updatedAnchorEl);
  };

  const handleClose = (calc) => {
    if (calc) {
      const updatedAnchorEl = Object.assign({}, anchorEl);
      delete updatedAnchorEl[calc.id];
      setAnchorEl(updatedAnchorEl);
    }
  };

  // Custom hook to get the previous state
  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  // Save the previous state
  const previousValues = usePrevious(currentValues);

  const defaultValues = {
    // "Amortization type": "Principal and interest",
    // "Payment frequency": "Monthly",
  };

  useEffect(() => {
    loadCalculation(
      defaultValues,
      externalCalc,
      externalExampleCalc,
      EscrowCalculatorQuestions,
      setQuestions,
      setAnswers,
      setCurrentValues,
      setShowUpdateFields
    );
    // let updatedAnswers = [];
    // let questions = EscrowCalculatorQuestions;

    // setQuestions(questions);

    // // extract current DSCR interest rates from capital page's data
    // // const DSCRRateRange = extractDSCRInterestRateRange(CapitalData);
    // // setDSCRRateRange(DSCRRateRange);

    // // if loaded on /my/calculations
    // // where we preload existing/saved calcs
    // if (externalCalc && externalCalc.Answers.length > 0) {
    //   // external calcs, can be 'new' calcs, that don't have answers, so other part of the if is relevant
    //   updatedAnswers = externalCalc.Answers.slice(0);

    //   updatedAnswers.forEach((answer) => {
    //     const [questionWithSameId] = questions.filter(
    //       (question) => question.q_id === answer.q_id
    //     );

    //     if (questionWithSameId) {
    //       // Update the answer with the corresponding question's properties
    //       answer.q_description = questionWithSameId.q_description;
    //       answer.q_title = questionWithSameId.q_title;
    //       answer.node_type = questionWithSameId.node_type;
    //       answer.a_type = questionWithSameId.a_type;
    //       answer.a_input_type = questionWithSameId.a_input_type;
    //       answer.a_opts = questionWithSameId.a_opts;
    //       answer.q_next_step_func = questionWithSameId.q_next_step_func;
    //       answer.q_validation_func = questionWithSameId.q_validation_func;
    //       answer.q_invalid_answer_help_text =
    //         questionWithSameId.q_invalid_answer_help_text;
    //       answer.a_error = questionWithSameId.a_error;
    //     }
    //   });

    //   setAnswers(updatedAnswers);

    //   setShowUpdateFields(true);
    // } else {
    //   let q_id_counter = "";
    //   let answers = [];
    //   const questionsSlice = questions.slice(); // make sure to make a copy
    //   let leftMostPath = questionsSlice.filter((question) => {
    //     // https://stackoverflow.com/a/21596128/1933331
    //     return /^0*$/.test(question.q_id);
    //   });

    //   // leftMostPath
    //   // must sort based on q_id length of 0s inside
    //   // left most paths q_ids
    //   leftMostPath.sort((a, b) => a.q_id.length - b.q_id.length);

    //   // leftMostPath for initial state
    //   // as user selects answers,
    //   // the path deviates and questions ahead change
    //   // this populates empty placeholder answers from initial questions
    //   for (let i = 0; i < leftMostPath.length; i++) {
    //     const question = leftMostPath[i];

    //     // create some defaults and pre-sets
    //     // to reduce users number of click
    //     let preset_a = "";
    //     if (Object.keys(externalExampleCalc).includes(question.q_title)) {
    //       preset_a = externalExampleCalc[question.q_title];
    //     }

    //     q_id_counter = q_id_counter + "0";
    //     answers.push({
    //       a_string: preset_a,
    //       q_id: new Array(i + 2).join("0"),
    //       q_description: question.q_description,
    //       q_title: question.q_title,
    //       node_type: question.node_type,
    //       a_type: question.a_type,
    //       a_input_type: question.a_input_type,
    //       a_opts: question.a_opts,
    //       q_next_step_func: question.q_next_step_func,
    //       q_validation_func: question.q_validation_func,
    //       q_invalid_answer_help_text: question.q_invalid_answer_help_text,
    //       a_error: question.a_error,
    //     });
    //   }

    //   updatedAnswers = answers;
    // }

    // setAnswers(updatedAnswers);

    // // based of answers set simple currentValues k:v
    // const initialCurrentValues = {};
    // updatedAnswers.forEach((answer) => {
    //   initialCurrentValues[answer.q_title] = answer.a_string;
    // });

    // setCurrentValues(initialCurrentValues);
  }, []);

  useEffect(() => {
    if (Object.keys(currentValues).length > 0) {
      const loanStart = answers?.filter((answer) => {
        return answer?.q_title === "Loan start";
      })?.[0]?.a_string;

      const payoutObj = {}; // has payoutkey:[mm/yyyy,mm/yyyy] to show when to do payouts
      const newSingleItemAnalysis = {};
      let cumulativeCushion = 0;

      Object.keys(payoutDict).forEach((key) => {
        const q_title = payoutDict[key];
        const firstPaymentDate = currentValues[q_title]?.[3];

        const frequency = currentValues[q_title]?.[1];

        if (frequency === "annual") {
          payoutObj[key] = [dayjs(firstPaymentDate, "YYYY-MM").format("MM/YY")];
        } else if (frequency === "semi-annual") {
          payoutObj[key] = [
            dayjs(firstPaymentDate, "YYYY-MM").format("MM/YY"),
            dayjs(firstPaymentDate, "YYYY-MM").add(6, "month").format("MM/YY"),
          ];
        } else if (frequency === "quarterly") {
          payoutObj[key] = [
            dayjs(firstPaymentDate, "YYYY-MM").format("MM/YY"),
            dayjs(firstPaymentDate, "YYYY-MM").add(3, "month").format("MM/YY"),
            dayjs(firstPaymentDate, "YYYY-MM").add(6, "month").format("MM/YY"),
            dayjs(firstPaymentDate, "YYYY-MM").add(9, "month").format("MM/YY"),
          ];
        } else if (frequency === "monthly") {
          payoutObj[key] = [];
          for (let i = 0; i < 12; i++) {
            payoutObj[key].push(
              dayjs(firstPaymentDate, "YYYY-MM").add(i, "month").format("MM/YY")
            );
          }
        }
      });

      const loanStartDate = dayjs(loanStart, "YYYY-MM");
      const finalMonth = loanStartDate.add(11, "month").format("MM/YY");

      Object.keys(payoutObj).forEach((key) => {
        payoutObj[key] = payoutObj[key].filter((date) =>
          dayjs(date, "MM/YY").isBefore(
            dayjs(finalMonth, "MM/YY").add(1, "day")
          )
        );
      });

      let monthlyPayInsObj = {};

      // calc monthly pay in, based on actual payments during dates of analysis
      let monthlyPayIn = 0;
      Object.keys(payoutObj).forEach((key) => {
        const dateArray = payoutObj[key];

        const q_title = payoutDict[key];

        const freq = currentValues[q_title][1];

        const freqObj = {
          annual: 1,
          "semi-annual": 2,
          quarterly: 4,
          monthly: 12,
        };

        const tFreq = freqObj[freq];

        const annualAmount = currentValues[q_title][0];

        if (annualAmount) {
          // also calc cumulative cushion

          if (dateArray.length === tFreq) {
            const monthlyAmount = annualAmount / 12;
            monthlyPayInsObj[key] = monthlyAmount;
            monthlyPayIn = monthlyPayIn + monthlyAmount;
          } else {
            // 2 - 1 = 1
            const actualPMTs = dateArray.length;
            const actualAmount = annualAmount / (tFreq / actualPMTs);

            const monthlyAmount = actualAmount / 12;

            monthlyPayInsObj[key] = monthlyAmount;
            monthlyPayIn = monthlyPayIn + monthlyAmount;
          }
        }
      });

      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      // convert all frequencies into date arrays

      // escrow account is necessary to fill in the gap when large payments out don't correspond equally to small monthly payments in, a cushion is often needed not to dip into negative

      const escrowData = [
        {
          payIn: null,
          payOut: null,
          noReserves: `$0`,
          withReserve: `$0`,
          withCushion: `$0`,
        },
      ];

      // set initial row for all individual calcs
      Object.keys(monthlyPayInsObj).map((key) => {
        // only if non 0 amount
        if (monthlyPayInsObj[key]) {
          newSingleItemAnalysis[key] = [
            {
              payIn: null,
              payOut: null,
              noReserves: 0,
              withReserve: 0,
              withCushion: 0,
            },
          ];

          const q_title = payoutDict[key];
          const cushionMonths = currentValues[q_title]?.[2];

          const amount = monthlyPayInsObj[key];

          const cushionAmount = cushionMonths * amount;
          cumulativeCushion = cumulativeCushion + cushionAmount;
        }
      });

      // first row of escrow data shows no cushion vs cushion
      let noReserves = 0;
      let maxDrawdown = 0;
      let maxDrawdownObj = {};

      for (let i = 0; i < 12; i++) {
        let thisMonthsPayOut = 0;
        const currentMonth = loanStartDate.add(i, "month");

        let outputObj = {};
        // get payouts information
        Object.keys(payoutObj).forEach((key) => {
          const dateArray = payoutObj[key];

          if (dateArray.includes(currentMonth.format("MM/YYYY"))) {
            const q_title = payoutDict[key];

            const freq = currentValues[q_title][1];

            const annualAmount = currentValues[q_title][0];
            let monthlyAmount = annualAmount / 12;
            let paymentAmount;
            switch (freq) {
              case "annual":
                paymentAmount = monthlyAmount * 12;
                break;
              case "semi-annual":
                paymentAmount = monthlyAmount * 6;
                break;
              case "quarterly":
                paymentAmount = monthlyAmount * 3;
                break;
              case "monthly":
                paymentAmount = monthlyAmount;
                break;
              default:
                paymentAmount = 0;
            }

            if (annualAmount) {
              thisMonthsPayOut += paymentAmount;
              outputObj[key] = paymentAmount;
              // also make an entry in the table for singleValueAnalysis
              // singleItemAnalysisData[key]
            }
          }
        });
        // As you loop through these months, add appropriate expenses into corresponding months.

        // As you loop also

        // calculate noReserves at each step, by adding monthlyPayIn and subtracting pay outs
        noReserves = noReserves + monthlyPayIn - thisMonthsPayOut;
        maxDrawdown = maxDrawdown < noReserves ? maxDrawdown : noReserves;

        escrowData.push({
          date: monthNames[currentMonth.month()],
          noReserves: noReserves,
          payIn: monthlyPayIn,
          payOut: thisMonthsPayOut,
          ...outputObj,
        });

        // For all single item analysis

        Object.keys(monthlyPayInsObj).map((key) => {
          // check that amount isnt 0
          if (monthlyPayInsObj[key]) {
            let newMonthData = {};
            if (Object.keys(outputObj).includes(key)) {
              newMonthData[key] = outputObj[key]; // payout amount to alignt to payout column
            }

            const previousNoReserves =
              newSingleItemAnalysis[key].slice(-1)[0].noReserves || 0;

            const monthlyPayIn = monthlyPayInsObj[key];
            const thisMonthsPayOut = outputObj[key] ? outputObj[key] : 0;
            const noReserves =
              previousNoReserves + monthlyPayIn - thisMonthsPayOut;

            maxDrawdownObj[key] =
              maxDrawdownObj[key] < noReserves
                ? maxDrawdownObj[key]
                : noReserves;

            newMonthData = {
              date: monthNames[currentMonth.month()],
              payIn: monthlyPayIn,
              payOut: thisMonthsPayOut,
              noReserves: noReserves,
              ...newMonthData,
            };

            // include payout for this single item analysis
            newSingleItemAnalysis[key].push(newMonthData);
          }
        });
      }

      // may useMemo to speed things up
      // 2nd loop / col calculation for cushion column
      for (let index = 0; index < escrowData.length; index++) {
        const monthData = escrowData[index];
        if (index === 0) {
          monthData.withReserve = Math.abs(maxDrawdown);
          monthData.withCushion = Math.abs(maxDrawdown) + cumulativeCushion;
        } else {
          monthData.withReserve =
            escrowData[index - 1].withReserve +
            monthData.payIn -
            monthData.payOut;
          monthData.withCushion =
            escrowData[index - 1].withCushion +
            monthData.payIn -
            monthData.payOut;
        }

        if (monthData.noReserves === maxDrawdown) {
          monthData.maxDrawdown = true;
        }
      }

      Object.keys(newSingleItemAnalysis).map((key) => {
        // 2nd loop for single iteam analysis

        const targetSingleData = newSingleItemAnalysis[key];

        if (targetSingleData) {
          // single analysis
          for (let index = 0; index < targetSingleData.length; index++) {
            const monthData = targetSingleData[index];
            const maxDrawdown = maxDrawdownObj[key];

            if (index === 0) {
              monthData.withReserve = Math.abs(maxDrawdown);

              const q_title = payoutDict[key];
              const cushion = currentValues[q_title]?.[2];
              const cushionAmount =
                currentValues[q_title]?.[2] * monthlyPayInsObj[key];
              cumulativeCushion = cumulativeCushion + cushionAmount;

              monthData.withCushion = Math.abs(maxDrawdown) + cushionAmount;
            } else {
              monthData.withReserve =
                targetSingleData[index - 1].withReserve +
                monthData.payIn -
                monthData.payOut;
              monthData.withCushion =
                targetSingleData[index - 1].withCushion +
                monthData.payIn -
                monthData.payOut;
            }

            if (monthData.noReserves === maxDrawdown) {
              monthData.maxDrawdown = true;
            }
          }
        }
      });

      // look through noReserves
      setEscrowResultsData(escrowData);
      setSingleItemAnalysisData(newSingleItemAnalysis);

      const newHUDSummary = {
        initialDeposit: escrowData[0].withCushion,
      };

      // payoutDict
      let cumulativeAmount = 0;
      Object.keys(newSingleItemAnalysis).forEach((payoutKey) => {
        const monthlyPayIn = newSingleItemAnalysis[payoutKey][1].payIn;

        const monthsOfPay = Math.round(
          newSingleItemAnalysis[payoutKey][0].withCushion / monthlyPayIn
        );

        newHUDSummary[payoutKey] = {
          months: monthsOfPay,
          monthlyAmount: monthlyPayIn,
          totalAmount: monthsOfPay * monthlyPayIn,
        };

        cumulativeAmount =
          cumulativeAmount + newHUDSummary[payoutKey].totalAmount;
      });

      newHUDSummary["aggregateAdj"] = {
        amount: escrowData[0].withCushion - cumulativeAmount,
      };

      setHUDSummary(newHUDSummary);
    }
  }, [currentValues]);

  const processSelect = (ltvRange) => {
    const updatedAnswers = answers.slice(0);

    const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
      return answer?.q_title === "Loan to value (LTV)";
    });

    toUpdateAnswer.a_string = ltvRange.LTV;

    const [toUpdateAnswerInterestRate] = updatedAnswers.filter((answer) => {
      return answer?.q_title === "Interest rate";
    });

    toUpdateAnswerInterestRate.a_string = (
      parseFloat(loanQuoteData.treasury5yr.rate) +
      parseFloat(ltvRange.creditSpread)
    ).toFixed(2);

    // clear interest rate in current values so value shows in textField
    const updatedCurrentValues = Object.assign({}, currentValues);

    // reset all current values
    Object.keys(updatedCurrentValues).forEach((key) => {
      updatedCurrentValues[key] = undefined;
    });

    setCurrentValues(updatedCurrentValues);

    // set answers last to recalc
    setAnswers(updatedAnswers);

    setTimeout(() => {
      scroller(`text_field_Purchase price`);
      setShowMeMyRate(false);
    }, 1000);
  };

  const handleUserSelection = (
    value,
    answer,
    answers,
    questions,
    setAnswers
  ) => {
    const questionsSlice = questions.slice();

    const updatedAnswers = answers.slice(0);

    const isValidAnswer = answer.q_validation_func(value, answers);

    if (isValidAnswer) {
      const targetAnswer = updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0];

      // save users answer
      targetAnswer.a_string = value;

      // reset error just in case
      targetAnswer.a_error = false;

      // determine if need to insert follow up questions
      // const next_q_id = answer.q_next_step_func(targetAnswer, answers);
      const next_q_id = answer.q_next_step_func(answer, answers, questions);

      const q_ids = updatedAnswers.map((answer) => {
        return answer.q_id;
      });

      // NEXT QUESTION is always next in position
      // NOT like before when it was based on length of q_id
      // this was limiting in terms of folding questions of the tree back
      // and allowing the tree to merge back to left most path
      const posOfNextQuestion =
        updatedAnswers
          .map((updatedAnswer) => {
            return updatedAnswer.q_id;
          })
          .indexOf(answer.q_id) + 1;

      const nextPotentialQuestion = updatedAnswers[posOfNextQuestion];

      if (
        next_q_id &&
        nextPotentialQuestion &&
        nextPotentialQuestion?.q_id !== next_q_id
      ) {
        // OFF LEFT MOST PATH
        const newNextQuestion = questionsSlice.filter((question) => {
          return question.q_id === next_q_id;
        })[0];

        // LEAF, remove all subsequent questions from answers
        if (newNextQuestion.node_type === "leaf") {
          // if a LEAF never delete anything (like using slice)
          // instead add hidden property
          for (let i = posOfNextQuestion; i < updatedAnswers.length; i++) {
            updatedAnswers[i].node_visibility = "hidden";
          }
        } else {
          // NODE, make sure subsequent questions are prents
          // need to make left most path starting from next questions's id.
          let leftMostPathFromCurrentNode = [];
          let deeperNextQuestion = newNextQuestion;
          let deeperNextQuestionChildrenLength =
            deeperNextQuestion.q_child_nodes.length;
          const map_q_id = questionsSlice.map((question) => {
            return question.q_id;
          });

          while (deeperNextQuestionChildrenLength !== 0) {
            deeperNextQuestion =
              questions[
                map_q_id.indexOf(
                  deeperNextQuestion.q_next_step_func(
                    deeperNextQuestion,
                    [],
                    []
                  )
                )
              ];

            if (deeperNextQuestion) {
              deeperNextQuestionChildrenLength =
                deeperNextQuestion.q_child_nodes.length;

              leftMostPathFromCurrentNode.push(deeperNextQuestion);
            } else {
              deeperNextQuestionChildrenLength = 0;
            }
          }

          const earlierQuestions = updatedAnswers.slice(
            0,
            posOfNextQuestion + 2
          );

          let counter = 0;
          for (
            let i = posOfNextQuestion + 1;
            i < leftMostPathFromCurrentNode.length + posOfNextQuestion + 1;
            i++
          ) {
            if (
              updatedAnswers[i]?.q_id === leftMostPathFromCurrentNode[counter]
            ) {
              //show the node
              // updatedAnswers[i].node_type = "node";
            } else {
              //or overwrite if different
              updatedAnswers[i] = leftMostPathFromCurrentNode[counter];
              // updatedAnswers[i].node_type = "node";
            }
            counter = counter + 1;
          }

          // make sure all nodes are re-updated
          // to have node_type be node so nothing is hidden
          // if people jump around
          for (let i = 0; i < updatedAnswers.length; i++) {
            if (updatedAnswers[i].node_type === "node") {
              updatedAnswers[i].node_visibility = "visible";
            }
          }
        }

        updatedAnswers[posOfNextQuestion] = questionsSlice.filter(
          (question) => {
            return question.q_id === next_q_id;
          }
        )[0];
        updatedAnswers[posOfNextQuestion].node_visibility = "visible";
      }

      setAnswers(updatedAnswers);
    } else {
      updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0].a_error = true;

      setAnswers(updatedAnswers);
      return;
    }
  };

  const generateAnswerOptions = (answer, index, answers) => {
    const questionsSlice = questions.slice();

    let answerOptionsUI;

    switch (answer.a_type) {
      case "leaf":
        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`} sx={{ mb: 2 }}>
            <div></div>
          </Box>
        );
        break;
      case "percent":
        let localValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : !isNaN(currentValues[answer.q_title])
            ? currentValues[answer.q_title]
            : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              margin: theme.spacing(1),
              [theme.breakpoints.down("md")]: {
                padding: `0 !important`,
              },
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{
                width: "100%",
                [theme.breakpoints.down("md")]: {
                  padding: `0 !important`,
                },
              }}
              error={answer.a_error}
              InputLabelProps={{ shrink: localValue }}
              label={answer.a_error ? "Error" : answer.q_title}
              type={answer.a_input_type}
              value={localValue || ""}
              onChange={(e) => {
                const num = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;

                setCurrentValues(updatedCurrentValues);

                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();

                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );

                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );

                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );

                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
            />
          </Box>
        );
        break;
      case "mc":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              flex: "0 1 100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {answer.a_opts(answers).map((a_opt, index) => {
              {
                return (
                  <Button
                    id={
                      index === 0
                        ? `text_field_${answer.q_title}_${
                            calc?.id ? calc.id : 0
                          }`
                        : `text_field_${answer.q_title}_opt_${index}`
                    }
                    sx={{ margin: theme.spacing(1) }}
                    variant={
                      answer.a_string === a_opt ? "contained" : "outlined"
                    }
                    onClick={(e) => {
                      const updatedCurrentValues = Object.assign(
                        {},
                        currentValues
                      );
                      updatedCurrentValues[answer.q_title] = a_opt;

                      setCurrentValues(updatedCurrentValues);

                      handleUserSelection(
                        a_opt,
                        answer,
                        answers,
                        questions,
                        setAnswers
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        const updatedCurrentValues = Object.assign(
                          {},
                          currentValues
                        );
                        updatedCurrentValues[answer.q_title] = a_opt;

                        setCurrentValues(updatedCurrentValues);

                        handleUserSelection(
                          a_opt,
                          answer,
                          answers,
                          questions,
                          setAnswers
                        );

                        const next_q_id = answer.q_next_step_func(
                          answer,
                          answers,
                          questions
                        );

                        if (next_q_id) {
                          const nextQuestion = questions.find(
                            (q) => q.q_id === next_q_id
                          );

                          const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                          const nextQuestionTextField = document.getElementById(
                            nextQuestionTextFieldId
                          );

                          if (nextQuestionTextField) {
                            nextQuestionTextField.focus();
                          }
                        }
                      }
                    }}
                  >
                    {a_opt}
                  </Button>
                );
              }
            })}
          </Box>
        );
        break;
      case "text":
        const localTextValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : currentValues[answer.q_title];

        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`}>
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{ margin: theme.spacing(5, 0) }}
              error={answer.a_error}
              label={answer.a_error ? "Error" : answer.q_title}
              type={answer.a_input_type}
              value={localTextValue}
              InputLabelProps={{ shrink: localTextValue }}
              onChange={(e) => {
                const val = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = val;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  val,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
            />
          </Box>
        );
        break;
      case "number":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={
                answer.a_error
                  ? answer.q_invalid_answer_help_text
                  : answer.q_help_text
                  ? answer.q_help_text
                  : ""
              }
              error={answer.a_error}
              label={answer.a_error ? "Error" : answer.q_title}
              type={answer.a_type_input}
              value={
                currentValues[answer.q_title] === undefined
                  ? answer.a_string
                    ? answer.a_string
                    : null
                  : currentValues[answer.q_title]
              }
              InputLabelProps={{
                shrink:
                  currentValues[answer.q_title] === undefined
                    ? answer.a_string
                      ? answer.a_string
                      : null
                    : currentValues[answer.q_title],
              }}
              onChange={(e) => {
                const value = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = value;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  value,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );

                // this is done to reset
                // 'get rate' output when new stuff is typed in
                setLoanQuoteData({});
              }}
            />
          </Box>
        );
        break;
      case "address":
        const address = calc?.Address;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
            sx={{ margin: theme.spacing(3, 0, 3, 0) }}
          >
            <AddressAutocomplete
              autocompleteOnKeyDown={(
                e,
                setSuggestionsVisible,
                setLocalAddress,
                place,
                locationList,
                setLocationList
              ) => {
                if (e.key === "Enter") {
                  setSuggestionChoice(place.formatted_address);

                  handleUserSelection(
                    place.formatted_address,
                    answer,
                    answers,
                    questions,
                    setAnswers
                  );
                }
              }}
              autocompleteOnClick={(
                e,
                setSuggestionsVisible,
                setLocalAddress,
                place,
                locationList,
                setLocationList
              ) => {
                setSuggestionChoice(place.formatted_address);

                handleUserSelection(
                  place.formatted_address,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              textFieldLabel={answer.a_error ? "Error" : answer.q_title}
              textFieldAriaLabel={"Enter address of the property for this loan"}
              searchOptionsTypes={[]}
              externalLocalAddress={address !== "" && address}
              error={answer.a_error}
            />
          </Box>
        );
        break;
      case "date":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
            }}
          >
            <>
              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={["month", "year"]}
                    label={"First payment"}
                    value={
                      currentValues[answer.q_title] === undefined
                        ? null
                        : dayjs(currentValues[answer.q_title])
                    }
                    onChange={(newDate) => {
                      const newDateString = newDate
                        ? newDate.format("YYYY-MM")
                        : "";

                      const updatedCurrentValues = Object.assign(
                        {},
                        currentValues
                      );
                      updatedCurrentValues[answer.q_title] = newDateString;

                      setCurrentValues(updatedCurrentValues);

                      handleUserSelection(
                        newDateString,
                        answer,
                        answers,
                        questions,
                        setAnswers
                      );
                    }}
                    onBlur={(newDate) => {
                      const newDateString = newDate
                        ? newDate.format("YYYY-MM")
                        : "";
                      processSubmission(newDateString);
                    }}
                    format="MM/YY"
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Box>
            </>
          </Box>
        );
        break;
      case "escrowRow":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              margin: theme.spacing(1),
              [theme.breakpoints.down("md")]: {
                flexDirection: "column",
              },
              gap: theme.spacing(1),
            }}
          >
            <TextField
              sx={{ flex: 1, flexGrow: 1 }}
              id={`text_field_${answer.q_title}_${
                calc?.id ? calc.id : 0
              }_escrow_row_amount`}
              error={answer.a_error}
              label={answer.a_error ? "Error" : "Annual amount"}
              type="text"
              value={
                currentValues?.[answer.q_title]?.[0]
                  ? `$${formatMoney(currentValues[answer.q_title][0], 0)}`
                  : "0"
              }
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.-]+/g, "");

                const updatedCurrentValues = { ...currentValues };

                if (
                  updatedCurrentValues[answer.q_title] &&
                  updatedCurrentValues[answer.q_title].length > 0
                ) {
                  updatedCurrentValues[answer.q_title][0] = value;
                } else {
                  updatedCurrentValues[answer.q_title] = [
                    value,
                    null,
                    null,
                    null,
                  ];
                }

                setCurrentValues(updatedCurrentValues);

                handleUserSelection(
                  updatedCurrentValues[answer.q_title],
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
            />
            <FormControl
              sx={{
                flexGrow: 1,
                flex: 1,
              }}
            >
              <InputLabel id={`dropdown_label_${answer.q_title}`}>
                Frequency
              </InputLabel>
              <Select
                labelId={`dropdown_label_${answer.q_title}`}
                id={`dropdown_${answer.q_title}`}
                value={
                  currentValues?.[answer.q_title]?.[1]
                    ? currentValues?.[answer.q_title]?.[1]
                    : null
                }
                label={answer.q_title}
                onChange={(e) => {
                  const value = e.target.value;
                  const updatedCurrentValues = Object.assign({}, currentValues);

                  if (
                    updatedCurrentValues[answer.q_title] &&
                    updatedCurrentValues[answer.q_title].length > 0
                  ) {
                    updatedCurrentValues[answer.q_title][1] = value;
                  } else {
                    updatedCurrentValues[answer.q_title] = [
                      null,
                      value,
                      null,
                      null,
                    ];
                  }

                  setCurrentValues(updatedCurrentValues);

                  handleUserSelection(
                    updatedCurrentValues[answer.q_title],
                    answer,
                    answers,
                    questions,
                    setAnswers
                  );
                }}
              >
                <MenuItem value={"annual"}>Annual</MenuItem>
                <MenuItem value={"semi-annual"}>Semi-annual</MenuItem>
                <MenuItem value={"quarterly"}>Quarterly</MenuItem>
                <MenuItem value={"monthly"}>Monthly</MenuItem>
              </Select>
            </FormControl>
            <FormControl
              sx={{
                flexGrow: 1,
                flex: 1,
              }}
            >
              <InputLabel id={`dropdown_label_${answer.q_title}`}>
                Cushion
              </InputLabel>

              <Select
                labelId={`dropdown_label_${answer.q_title}`}
                id={`dropdown_${answer.q_title}`}
                value={currentValues?.[answer.q_title]?.[2]}
                label={answer.q_title}
                onChange={(e) => {
                  const value = e.target.value;
                  const updatedCurrentValues = Object.assign({}, currentValues);

                  if (
                    updatedCurrentValues[answer.q_title] &&
                    updatedCurrentValues[answer.q_title].length > 0
                  ) {
                    updatedCurrentValues[answer.q_title][2] = value;
                  } else {
                    updatedCurrentValues[answer.q_title] = [
                      null,
                      null,
                      value,
                      null,
                    ];
                  }

                  setCurrentValues(updatedCurrentValues);
                  handleUserSelection(
                    updatedCurrentValues[answer.q_title],
                    answer,
                    answers,
                    questions,
                    setAnswers
                  );
                }}
              >
                <MenuItem value={0}>0</MenuItem>
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3</MenuItem>
                <MenuItem value={4}>4</MenuItem>
                <MenuItem value={5}>5</MenuItem>
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ flex: 1, flexGrow: 1 }}
                views={["month", "year"]}
                format="MM/YY"
                label={`First payment`}
                value={
                  currentValues?.[answer.q_title]?.[3]
                    ? dayjs(currentValues?.[answer.q_title]?.[3])
                    : null
                }
                onChange={(newDate) => {
                  const value = newDate;
                  const updatedCurrentValues = Object.assign({}, currentValues);

                  if (
                    updatedCurrentValues[answer.q_title] &&
                    updatedCurrentValues[answer.q_title].length > 0
                  ) {
                    updatedCurrentValues[answer.q_title][3] = value;
                  } else {
                    updatedCurrentValues[answer.q_title] = [
                      null,
                      null,
                      null,
                      value,
                    ];
                  }

                  setCurrentValues(updatedCurrentValues);
                  handleUserSelection(
                    updatedCurrentValues[answer.q_title],
                    answer,
                    answers,
                    questions,
                    setAnswers
                  );
                }}
                onBlur={(newDate) => {
                  processSubmission(newDate);
                }}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </Box>
        );
        break;
      default:
    }

    return answerOptionsUI;
  };

  const showError =
    loanQuoteData?.LTVObj &&
    (Object.keys(loanQuoteData.LTVObj).length === 0 ||
      creditScore <
        loanQuoteData.LTVObj[Object.keys(loanQuoteData.LTVObj)[0]]
          .creditScoreCutoff);

  const resetCalculator = () => {
    // Loop through all answers and set a_string to ""
    const updatedAnswers = answers.map((answer) => ({
      ...answer,
      a_string: "",
    }));

    // Reset current values to ""
    const updatedCurrentValues = Object.keys(currentValues).reduce(
      (acc, key) => {
        acc[key] = ""; // Set each current value to an empty string
        return acc;
      },
      {}
    );

    setCurrentValues(updatedCurrentValues); // Update current values in state
  };

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const saveOrUpdate = async (isPDFGenerated = false) => {
    const targetUserID = userProfile.UID;

    let savedOrUpdated = "saved";
    const calcData = {}; // not used for escrow

    let updatedCalc;
    if (!calc) {
      updatedCalc = await createCalc(
        user,
        targetUserID,
        answers,
        calcType,
        calcData,
        true,
        isPDFGenerated
      );
      // send an email to the user
      // with their calc details
      // so they have a way to return to it
      // this is done on the backend now, if query param l=1 is passed to createCalc
    } else {
      savedOrUpdated = "updated";
      const name = answers?.filter((answer) => {
        return answer?.q_title === "Calculation name";
      })?.[0]?.a_string;

      const address = answers?.filter((answer) => {
        return answer?.q_title === "Address (optional)";
      })?.[0]?.a_string;

      updatedCalc = await updateCalc(user, calc, {
        Answers: answers,
        CalcData: calcData,
        Name: name,
        Address: address,
        PDFGenerated: isPDFGenerated,
      });
    }

    setCalc(updatedCalc);

    if (!showUpdateFields) {
      setShowCreatedSuccessMessage(true);
    }

    if (!isPDFGenerated) {
      // only show this if not concurently generating the pdf
      setSnackbarData({
        message: `Calculation ${savedOrUpdated}`,
        options: {
          variant: "success",
          autoHideDuration: 5000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        },
      });
    }

    gtag.event({ action: "escrow_calc-saved" });
  };
  const handlePDFDownload = async () => {
    try {
      setSnackbarData({
        message: "PDF download initiated",
        options: {
          variant: "success",
          autoHideDuration: 5000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        },
      });

      // Dynamically import the libraries inside the function
      const jsPDFModule = await import("jspdf");
      const html2canvasModule = await import("html2canvas");

      const jsPDF = jsPDFModule.default || jsPDFModule.jsPDF;
      const html2canvas = html2canvasModule.default;

      // Create a new jsPDF instance inside the function
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;
      let currentY = 30; // Start position after header

      // Add OfferMarket header to first page
      doc.setFillColor(theme.palette.primary.main);
      doc.rect(0, 0, pageWidth, 20, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.text("OfferMarket - Escrow calculator", margin, 13);

      // Reset text color for the rest of the document
      doc.setTextColor(0, 0, 0);

      // Add title
      doc.setFontSize(18);
      doc.text("Escrow inputs", margin, currentY);
      currentY += 10;

      // Store original viewport width to restore later
      const originalViewportMeta = document.querySelector(
        'meta[name="viewport"]'
      );

      // Temporarily modify viewport for better rendering on mobile
      if (isMobile) {
        // Set a fixed viewport width for consistent rendering
        const viewportMeta = document.querySelector('meta[name="viewport"]');
        if (viewportMeta) {
          viewportMeta.content = "width=1024, initial-scale=1";
        } else {
          // Create viewport meta if it doesn't exist
          const newMeta = document.createElement("meta");
          newMeta.name = "viewport";
          newMeta.content = "width=1024, initial-scale=1";
          document.head.appendChild(newMeta);
        }
      }

      // First capture the inputs section
      const inputsElement = document.getElementById("escrow-calculator-input");

      if (inputsElement) {
        // Find all elements with no-print-element class and hide them
        const noPrintElements = document.querySelectorAll(".no-print-element");
        const hiddenElements = [];

        // Store original display values and hide elements
        noPrintElements.forEach((element) => {
          hiddenElements.push({
            element: element,
            display: element.style.display,
          });
          element.style.display = "none";
        });

        // Hide escrow rows with zero or null amounts
        const zeroAmountRows = [];

        // Find all escrow row containers - improved selector to better target escrow rows
        const escrowRowContainers = inputsElement.querySelectorAll(
          "div > div > div > div"
        );

        // First identify all escrow-related titles from firstPartOfQuestions
        const escrowTitles = firstPartOfQuestions.filter(
          (title) => title !== "Loan start" // Exclude non-escrow items
        );

        escrowRowContainers.forEach((container) => {
          // Look for title elements that might be escrow items
          const titleElement = container.querySelector("h2");
          if (titleElement) {
            const title = titleElement.textContent.trim();

            // Check if this is an escrow item by checking against our known escrow titles
            if (escrowTitles.includes(title)) {
              // Check if the value is empty, zero, or null in currentValues
              if (
                !currentValues[title] ||
                (Array.isArray(currentValues[title]) &&
                  (!currentValues[title][0] ||
                    currentValues[title][0] === "0")) ||
                currentValues[title] === "0" ||
                currentValues[title] === ""
              ) {
                // This is an escrow row with zero/null amount - hide the entire container
                zeroAmountRows.push({
                  element: container,
                  display: container.style.display,
                });
                container.style.display = "none";
              }
            }
          }
        });

        // Store original styles for mobile optimization
        const originalStyles = [];
        if (isMobile) {
          // Find elements that need style adjustments for PDF
          const mobileElements = inputsElement.querySelectorAll(
            '[class*="MuiBox-root"]'
          );
          mobileElements.forEach((element) => {
            // Store original styles
            originalStyles.push({
              element,
              display: element.style.display,
              flexDirection: element.style.flexDirection,
              width: element.style.width,
              margin: element.style.margin,
              padding: element.style.padding,
            });

            // Optimize layout for PDF
            if (element.style.flexDirection === "column") {
              element.style.flexDirection = "row";
            }
            if (
              element.style.width === "100%" ||
              element.style.width === "auto"
            ) {
              element.style.width = "fit-content";
            }
            // Adjust margins and padding for better PDF layout
            element.style.margin = "5px";
            element.style.padding = "5px";
          });
        }

        const inputsCanvas = await html2canvas(inputsElement, {
          scale: isMobile ? 1.5 : 2, // Adjust scale based on device
          useCORS: true,
          logging: false,
          windowWidth: isMobile ? 1024 : window.innerWidth, // Force wider rendering on mobile
          onclone: (clonedDoc) => {
            // Additional adjustments to the cloned document before rendering
            if (isMobile) {
              const tables = clonedDoc.querySelectorAll("table");
              tables.forEach((table) => {
                table.style.width = "100%";
                table.style.tableLayout = "fixed";
              });
            }
          },
        });

        // Restore visibility after capturing
        noPrintElements.forEach((element, index) => {
          element.style.display = hiddenElements[index].display;
        });

        // Restore hidden escrow rows
        zeroAmountRows.forEach(({ element, display }) => {
          element.style.display = display;
        });

        // Restore original styles
        if (originalStyles.length > 0) {
          originalStyles.forEach((item) => {
            item.element.style.display = item.display;
            item.element.style.flexDirection = item.flexDirection;
            item.element.style.width = item.width;
            item.element.style.margin = item.margin;
            item.element.style.padding = item.padding;
          });
        }

        const inputsImgData = inputsCanvas.toDataURL("image/png");
        const inputsImgWidth = contentWidth;
        const inputsImgHeight =
          (inputsCanvas.height * inputsImgWidth) / inputsCanvas.width;

        // Check if inputs section fits on first page, otherwise add a new page
        if (currentY + inputsImgHeight > pageHeight - margin) {
          doc.addPage();
          // Add header to new page
          doc.setFillColor(theme.palette.primary.main);
          doc.rect(0, 0, pageWidth, 20, "F");
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(16);
          doc.text("OfferMarket - Escrow calculator", margin, 13);
          doc.setTextColor(0, 0, 0);
          currentY = 30;
        }

        doc.addImage(
          inputsImgData,
          "PNG",
          margin,
          currentY,
          inputsImgWidth,
          inputsImgHeight
        );
        currentY += inputsImgHeight + 15;

        // Always start on a new page for the analysis sections
        doc.addPage();
        // Add header to new page
        doc.setFillColor(theme.palette.primary.main);
        doc.rect(0, 0, pageWidth, 20, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.text("OfferMarket - Escrow calculator", margin, 13);
        doc.setTextColor(0, 0, 0);

        // Add title for analysis section
        doc.setFontSize(18);
        doc.text("Aggregate Analysis", margin, 30);
        currentY = 40;

        // Hide all no-print-elements in the entire document for the rest of the capture
        const allNoPrintElements =
          document.querySelectorAll(".no-print-element");
        allNoPrintElements.forEach((element) => {
          element.style.display = "none";
        });

        // Capture the main content (aggregate analysis)
        const contentElement = document.getElementById(
          "escrow-calculator-content"
        );
        if (contentElement) {
          // Apply temporary styles for better table rendering
          const tables = contentElement.querySelectorAll("table");
          const originalTableStyles = [];

          tables.forEach((table) => {
            originalTableStyles.push({
              element: table,
              width: table.style.width,
              tableLayout: table.style.tableLayout,
              fontSize: table.style.fontSize,
            });

            // Optimize tables for PDF
            table.style.width = "100%";
            table.style.tableLayout = "fixed";
            table.style.fontSize = isMobile ? "12px" : "14px";

            // Adjust cell widths for better fit
            const cells = table.querySelectorAll("th, td");
            cells.forEach((cell) => {
              cell.style.padding = "4px";
              cell.style.whiteSpace = "normal";
              cell.style.wordBreak = "break-word";
            });
          });

          // Get all analysis sections
          const analysisElements = contentElement.children;

          // First capture the aggregate analysis (first child)
          if (analysisElements.length > 0) {
            const aggregateElement = analysisElements[0];

            const aggregateCanvas = await html2canvas(aggregateElement, {
              scale: isMobile ? 1.5 : 2,
              useCORS: true,
              logging: false,
              windowWidth: isMobile ? 1024 : window.innerWidth,
            });

            const aggregateImgData = aggregateCanvas.toDataURL("image/png");
            const aggregateImgWidth = contentWidth;
            const aggregateImgHeight =
              (aggregateCanvas.height * aggregateImgWidth) /
              aggregateCanvas.width;

            doc.addImage(
              aggregateImgData,
              "PNG",
              margin,
              currentY,
              aggregateImgWidth,
              aggregateImgHeight
            );
          }

          // Capture each individual analysis section
          for (let i = 1; i < analysisElements.length; i++) {
            // Start a new page for each analysis
            doc.addPage();
            // Add header to new page
            doc.setFillColor(theme.palette.primary.main);
            doc.rect(0, 0, pageWidth, 20, "F");
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(16);
            doc.text("OfferMarket - Escrow calculator", margin, 13);
            doc.setTextColor(0, 0, 0);

            const element = analysisElements[i];

            // Get the title from the element
            const titleElement = element.querySelector("h2");
            const title = titleElement
              ? titleElement.textContent
              : `Analysis ${i}`;

            // Add title for this analysis section
            doc.setFontSize(18);
            doc.text(title, margin, 30);
            currentY = 40;

            const canvas = await html2canvas(element, {
              scale: isMobile ? 1.5 : 2,
              useCORS: true,
              logging: false,
              windowWidth: isMobile ? 1024 : window.innerWidth,
            });

            const imgData = canvas.toDataURL("image/png");
            const imgWidth = contentWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            doc.addImage(imgData, "PNG", margin, currentY, imgWidth, imgHeight);
          }

          // Restore original table styles
          originalTableStyles.forEach((item) => {
            item.element.style.width = item.width;
            item.element.style.tableLayout = item.tableLayout;
            item.element.style.fontSize = item.fontSize;
          });
        }

        // Add HUD summary if available
        if (Object.keys(HUDSummary).length > 0) {
          const hudElement = document.getElementById("hud-form-content");
          if (hudElement) {
            // Start a new page for HUD summary
            doc.addPage();
            // Add header to new page
            doc.setFillColor(theme.palette.primary.main);
            doc.rect(0, 0, pageWidth, 20, "F");
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(16);
            doc.text("OfferMarket - Escrow calculator", margin, 13);
            doc.setTextColor(0, 0, 0);

            // Add title for HUD summary
            doc.setFontSize(18);
            doc.text("HUD Summary", margin, 30);
            currentY = 40;

            // Apply temporary styles for better HUD form rendering
            const originalHUDStyles = [];
            const hudFormElements = hudElement.querySelectorAll(
              '[class*="MuiBox-root"], [class*="MuiGrid-root"], table, th, td'
            );

            // Fix for HUD table getting cut off - adjust the table width and layout
            const hudTables = hudElement.querySelectorAll("table");
            hudTables.forEach((table) => {
              originalHUDStyles.push({
                element: table,
                width: table.style.width,
                tableLayout: table.style.tableLayout,
                fontSize: table.style.fontSize,
                maxWidth: table.style.maxWidth,
                overflowX: table.style.overflowX,
              });

              // Make sure table fits within PDF width
              table.style.width = "95%";
              table.style.maxWidth = "95%";
              table.style.tableLayout = "fixed";
              table.style.overflowX = "visible";
              table.style.fontSize = isMobile ? "10px" : "12px";

              // Adjust cell widths to prevent content from being cut off
              const cells = table.querySelectorAll("th, td");
              cells.forEach((cell) => {
                cell.style.padding = "2px";
                cell.style.whiteSpace = "normal";
                cell.style.wordBreak = "break-word";
                cell.style.fontSize = isMobile ? "10px" : "12px";

                // If this is a numeric cell, align right and ensure it fits
                if (cell.textContent.match(/^\$?[\d,.]+$/)) {
                  cell.style.textAlign = "right";
                  cell.style.width = "auto";
                }
              });
            });

            hudFormElements.forEach((element) => {
              if (
                !originalHUDStyles.some((style) => style.element === element)
              ) {
                originalHUDStyles.push({
                  element,
                  display: element.style.display,
                  width: element.style.width,
                  margin: element.style.margin,
                  padding: element.style.padding,
                  fontSize: element.style.fontSize,
                  overflow: element.style.overflow,
                  whiteSpace: element.style.whiteSpace,
                });
              }

              // Optimize layout for PDF
              element.style.margin = "3px";
              element.style.padding = "3px";

              // Ensure container elements are wide enough
              if (element.tagName === "DIV") {
                element.style.width = "100%";
                element.style.maxWidth = "100%";
              }
            });

            // Add extra margin for HUD summary in mobile view
            if (isMobile) {
              hudElement.style.marginTop = "20px";
              hudElement.style.marginBottom = "20px";
            }

            // Ensure the HUD element has proper width
            hudElement.style.width = "100%";
            hudElement.style.maxWidth = "100%";

            const hudCanvas = await html2canvas(hudElement, {
              scale: isMobile ? 1.2 : 1.5, // Slightly lower scale to fit better
              useCORS: true,
              logging: false,
              windowWidth: 1200, // Force wider rendering for HUD table
              onclone: (clonedDoc) => {
                // Additional adjustments to the cloned document before rendering
                const hudTables = clonedDoc.querySelectorAll(
                  "#hud-form-content table"
                );
                hudTables.forEach((table) => {
                  table.style.width = "95%";
                  table.style.maxWidth = "95%";
                  table.style.tableLayout = "fixed";
                  table.style.marginTop = "15px";
                  table.style.marginBottom = "15px";

                  // Ensure all cells have proper styling
                  const cells = table.querySelectorAll("th, td");
                  cells.forEach((cell) => {
                    cell.style.padding = "2px";
                    cell.style.fontSize = isMobile ? "10px" : "12px";
                  });
                });
              },
            });

            // Restore original HUD styles
            originalHUDStyles.forEach((item) => {
              item.element.style.display = item.display;
              item.element.style.width = item.width;
              item.element.style.margin = item.margin;
              item.element.style.padding = item.padding;
              item.element.style.fontSize = item.fontSize;
              item.element.style.overflow = item.overflow;
              item.element.style.whiteSpace = item.whiteSpace;
              if (item.maxWidth) item.element.style.maxWidth = item.maxWidth;
              if (item.tableLayout)
                item.element.style.tableLayout = item.tableLayout;
              if (item.overflowX) item.element.style.overflowX = item.overflowX;
            });

            // Remove the extra margin we added
            if (isMobile) {
              hudElement.style.marginTop = "";
              hudElement.style.marginBottom = "";
            }
            hudElement.style.width = "";
            hudElement.style.maxWidth = "";

            const hudImgData = hudCanvas.toDataURL("image/png");
            const hudImgWidth = contentWidth;
            const hudImgHeight =
              (hudCanvas.height * hudImgWidth) / hudCanvas.width;

            // If the HUD table is too tall for one page, adjust the scale
            if (hudImgHeight > pageHeight - 60) {
              // Use a smaller scale to fit on one page
              const adjustedHeight = pageHeight - 60;
              const adjustedWidth =
                (adjustedHeight * hudCanvas.width) / hudCanvas.height;

              doc.addImage(
                hudImgData,
                "PNG",
                (pageWidth - adjustedWidth) / 2, // Center horizontally
                currentY,
                adjustedWidth,
                adjustedHeight
              );
            } else {
              doc.addImage(
                hudImgData,
                "PNG",
                margin,
                currentY,
                hudImgWidth,
                hudImgHeight
              );
            }
          }
        }

        // Restore all no-print-elements
        allNoPrintElements.forEach((element) => {
          element.style.display = "";
        });
      }

      // Add footer with date on each page
      const pageCount = doc.internal.getNumberOfPages();
      const today = new Date();
      const dateStr = today.toLocaleDateString();

      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(
          `Generated on ${dateStr} via OfferMarket.us/blog/escrow-calculator | Page ${i} of ${pageCount}`,
          margin,
          pageHeight - 10
        );
      }

      // Save the PDF
      const calculationName =
        answers?.filter((answer) => answer?.q_title === "Calculation name")?.[0]
          ?.a_string || "Escrow-Calculation";

      doc.save(`${calculationName.replace(/\s+/g, "-")}.pdf`);

      if (isMobile) {
        // Save or update the calculation before reloading
        try {
          if (calc?.id) {
            // If calculation exists, update it
            await updateCalc({
              id: calc.id,
              answers: answers,
              name: calculationName,
              type: "escrow",
            });
          } else {
            // If new calculation, create it
            await createCalc({
              answers: answers,
              name: calculationName,
              type: "escrow",
            });
          }

          // Log the save event
          gtag.event({
            action: "escrow_calc-saved_before_reload",
          });
        } catch (saveError) {
          console.error("Error saving calculation before reload:", saveError);
        }
        window.location.reload();
      }

      gtag.event({
        action: "escrow_calc-pdf_created",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);

      setSnackbarData({
        message: "Error generating PDF. Please try again.",
        options: {
          variant: "error",
          autoHideDuration: 5000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        },
      });
    }
  };

  return (
    <Box
      key={`calculation_${externalCalc?.id ? externalCalc.id : 0}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        margin: theme.spacing(0, "auto"),
        width: "100%",
      }}
    >
      {router.pathname.includes("/my/calculation") && calc && (
        <Box sx={{ width: "100%", display: "flex" }}>
          <IconButton
            sx={{
              margin: theme.spacing("auto", 0.5, "auto", "auto"),
            }}
            key={`icon_button_delete_calc_${calc.id}`}
            aria-label="loan settings"
          >
            <MoreVertIcon
              key={`vert_icon_delete_calc_${calc.id}`}
              aria-controls={
                Boolean(anchorEl[calc.id]) ? "basic-menu" : undefined
              }
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl[calc.id]) ? "true" : undefined}
              onClick={(e) => handleOpen(e, calc)}
            />
            <Menu
              key={`menu_delete_calc_${calc.id}`}
              anchorEl={anchorEl[calc.id]}
              open={Boolean(anchorEl[calc.id])}
              onClose={() => handleClose(calc)}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem key={`menu_item_delete_${calc.id}`}>
                <Button
                  sx={{ width: "100%" }}
                  variant="contained"
                  color="error"
                  onClick={() => {
                    (async () => {
                      await updateCalc(user, calc, {
                        Deleted: true,
                      });

                      handleClose(calc);

                      await sleep(1000); // BE deleted flag need a sec to return on next call
                      await loadCalcs(refCalcs);
                    })();
                  }}
                >
                  Delete calculation
                </Button>
              </MenuItem>
            </Menu>
          </IconButton>
        </Box>
      )}

      {answers.length > 0 ? (
        <Box id={"calculator-to-print"}>
          <Box id={"pdf-1-user-input"}>
            {answers
              .filter((answer) => {
                // filtering out the question that we don't want to show in this part of the UI
                return firstPartOfQuestions.includes(answer.q_title);
              })
              .map((answer, index) => {
                return (
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        [theme.breakpoints.down("md")]: {
                          margin: theme.spacing(1),
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: theme.spacing(0.5),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          flex: "1 1 35%",
                          display: "flex",
                          flexDirection: "row",
                          [theme.breakpoints.down("md")]: {
                            flex: "0 0 50%",
                          },
                        }}
                      >
                        <ButtonBase
                          sx={{
                            width: "100%",
                            padding: theme.spacing(1),
                            "&:focus": {
                              outline: "none",
                              borderRadius: theme.spacing(0.5),
                              boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                                theme.palette.info.main
                              }`,
                            },
                            [theme.breakpoints.down("md")]: {
                              margin: theme.spacing(1),
                              padding: 0,
                            },
                          }}
                          onClick={() => {
                            let updatedShowQuestionDescription = Object.assign(
                              {},
                              showQuestionDescription
                            );

                            updatedShowQuestionDescription[
                              `${answer.q_title}`
                            ] =
                              !updatedShowQuestionDescription[
                                `${answer.q_title}`
                              ];

                            setShowQuestionDescription(
                              updatedShowQuestionDescription
                            );
                          }}
                        >
                          <Typography
                            variant="h2"
                            sx={{
                              fontSize: theme.spacing(3),
                              textAlign: "left",
                              [theme.breakpoints.down("md")]: {
                                fontSize: theme.spacing(2),
                              },
                            }}
                          >
                            {answer.q_title}
                          </Typography>
                          {showQuestionDescription[`${answer.q_title}`] ? (
                            <KeyboardArrowUpIcon
                              sx={{
                                display: "flex",
                                margin: theme.spacing(
                                  "auto",
                                  1,
                                  "auto",
                                  "auto"
                                ),
                                fontSize: theme.spacing(2.5),
                              }}
                            />
                          ) : (
                            <InfoOutlinedIcon
                              sx={{
                                display: "flex",
                                margin: theme.spacing(
                                  "auto",
                                  1,
                                  "auto",
                                  "auto"
                                ),
                                fontSize: theme.spacing(2.5),
                              }}
                              color="info"
                            />
                          )}
                        </ButtonBase>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flex: "1 1 75%",
                          justifyContent: "space-between",
                          [theme.breakpoints.down("md")]: {
                            flex: "0 0 50%",
                          },
                        }}
                      >
                        {generateAnswerOptions(answer, index, answers)}
                        {/* Remove the Reset button from the PDF export by adding a className */}
                        {index === 0 && (
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={resetCalculator}
                            sx={{
                              margin: theme.spacing(1),
                              "@media print": { display: "none" }, // Hide when printing
                            }}
                            className="no-print-element" // Add a class to identify it for PDF generation
                          >
                            Reset
                          </Button>
                        )}
                      </Box>
                    </Box>
                    <Collapse in={showQuestionDescription[`${answer.q_title}`]}>
                      <Box sx={{ width: "100%" }}>
                        <Typography
                          variant="h2"
                          sx={{
                            margin: theme.spacing(1),
                            fontSize: theme.spacing(1.8),
                            [theme.breakpoints.down("md")]: {
                              margin: theme.spacing(1, 0, 1, 1),
                            },
                          }}
                        >
                          {answer?.q_description(answers)}
                        </Typography>
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
          </Box>

          <Box
            sx={{
              margin: theme.spacing(1, 0, 0, 0),
              padding: theme.spacing(1, 0),
              [theme.breakpoints.down("md")]: {
                padding: theme.spacing(1),
              },
            }}
          >
            <Box id="pdf-2">
              <Box sx={{ margin: theme.spacing(3, 0) }}>
                <Typography
                  className="no-print-element"
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: 0,
                    "@media print": { display: "none" },
                  }}
                >
                  Aggregate Analysis
                </Typography>
                <EscrowExplanationTable
                  escrowResultsData={escrowResultsData}
                  payoutDict={payoutDict}
                />
              </Box>

              {Object.keys(singleItemAnalysisData).map((payoutKey) => {
                {
                  return (
                    <Box sx={{ margin: theme.spacing(3, 0) }}>
                      <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                          fontWeight: "bold",
                          marginBottom: 0,
                          "@media print": { display: "none" },
                        }}
                        className="no-print-element"
                      >
                        {payoutDict[payoutKey]} analysis
                      </Typography>
                      <EscrowExplanationTable
                        escrowResultsData={singleItemAnalysisData[payoutKey]}
                        payoutDict={payoutDict}
                        singleAnalysis={payoutKey}
                      />
                    </Box>
                  );
                }
              })}
            </Box>
            <Box id="pdf-3">
              {Object.keys(HUDSummary).length > 0 && (
                <HUDForm
                  HUDSummary={HUDSummary}
                  payoutDict={payoutDict}
                  singleItemAnalysisData={singleItemAnalysisData}
                />
              )}
            </Box>

            <Box id={"pdf-4"}>
              <SaveSharePDF
                answers={answers}
                allowedQuestionTitles={thirdPart} // <>!!!
                generateAnswerOptions={generateAnswerOptions}
                calc={calc}
                setCalc={setCalc}
                saveOrUpdate={saveOrUpdate}
                calculatorType={calcType}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{ margin: theme.spacing(2, 0), width: "100%", minHeigh: "100vh" }}
        >
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
        </Box>
      )}
    </Box>
  );
}
